/**
 * M7 S21: the four "Transit Priority" paths (spec §8.2, §8.3 "Sim pressure (writes)"; canon §4 Finale). Each path's
 * act becomes Career changes plus `StoryCommand`s; the faction sim decides the outcome through pressure, votes and
 * floors, never `forceRelation` (plan §3). `ladder.ts` decides whether a commit or a move is allowed, pays the common
 * Blackmail exit, and calls these.
 *
 * - **Expose:** `proposeCharterVote(tetherstoneBan, sponsor, evidenceWeight)` over the bundle picked for Expose. The
 *   sponsor is the player, except after Guardians (the pilot is wanted): the player corporation if founded, else the
 *   Mutual (Kell's leak, `finale.json expose.fallbackSponsor`). `hale.ts exposeAuthority` runs once at the hand-in:
 *   it voids a live Hale deal, seeds Hale's enmity bounty when he was refused, and sets `authority_exposed` only with
 *   the original 50-A or Duarte. Tethys's retaliation lands (once, `landRetaliation`). The vote starts `pending`.
 * - **Blackmail:** `voss_blackmailed`; the stipend's inputs are fixed at commit (AI-only Tethys income over 10 faction
 *   days × the share; S25 pays and decays it). No retaliation: Voss is paying.
 * - **Guardians:** the guardian aggression floor (a `floor` countdown, so it is re-sent on re-apply and extended past
 *   an absence), the cell's treasury through `hiddenTransfer`, the Tethys security response (Tethys → guardian
 *   pressure), the Authority bounty that makes the pilot wanted, `guardian_joined`, the corporation's claims
 *   `harbouring`, and Tethys's retaliation (once).
 * - **Sell:** Meridian → Tethys pressure, the Meridian/Tethys grievance floor (a `floor` countdown), the payout (× the
 *   "damaged goods" factor after Blackmail). Tethys retaliates only after Blackmail (Voss blames the player).
 *
 * Retaliation lands once (§8.3): the first path that retaliates seeds Tethys → player pressure and lands
 * `leverage.ts applyRetaliation`; a later path applies only what `applyRetaliation` finds new (a harsher row set or
 * a higher multiplier), never the seed again.
 *
 * Pure (Career in, Career out); event-driven, never per frame.
 */
import { withWallet } from '../../career/ops'
import type { Career } from '../../career/types'
import type { FactionId, MeasureName } from '../../strategic/types'
import type { CaseData } from '../bundle'
import { evidenceWeight } from '../case'
import { pathsInForce, syncClock } from '../clock'
import { commsConfig } from '../comms'
import { contactFor, type ContactResolution, type ContactView } from '../contacts'
import { floorRef, scheduleCountdown } from '../countdowns'
import { storyData } from '../data'
import { exposeAuthority, haleData, type HaleData } from '../hale'
import { applyRetaliation, type LeverageData } from '../leverage'
import type { ClockConfig, ContactsConfig, FinaleConfig } from '../schemas'
import { setFlag, storyOf, withStory } from '../state'
import type {
  FinalePath,
  StoryCommand,
  StoryEffect,
  StoryFinale,
  StoryResult,
  StoryStipend,
  StoryWorldView,
  VoteOutcome,
} from '../types'

// ---------------------------------------------------------------------------------------------
// Data and input
// ---------------------------------------------------------------------------------------------

/** What the finale reads: `finaleData()` gives the shipped files (tests pass their own). */
export interface FinaleData {
  finale: FinaleConfig
  case: CaseData
  leverage: LeverageData
  hale: HaleData
  clock: ClockConfig
}

let cached: FinaleData | undefined

/** The shipped data, assembled once. */
export function finaleData(): FinaleData {
  if (cached === undefined) {
    const sd = storyData()
    cached = {
      finale: sd.finale,
      case: { logsById: sd.logsById, case: sd.case },
      leverage: { leverage: sd.leverage, logging: sd.logging },
      hale: haleData(),
      clock: sd.clock,
    }
  }
  return cached
}

/** What the worker and the world tell a commit (the story link fills it from `StoryView` and the player's ship). */
export interface FinaleInput {
  view: Pick<StoryWorldView, 'corpFaction' | 'tethysAiTetherIncome10d'>
  /** The player's system (where an Asset Recovery hunt starts). */
  systemId: string
  /** Credits outstanding on TCF fleet notes (0 = none; spec §9 stage). */
  fleetNotesPrincipal: number
  /** The player corporation's claim ids (the Guardians path makes them `harbouring`). Absent = none known. */
  corpClaimIds?: readonly string[]
}

/** How a path is reached: a first commit, or a ladder move (`from`), and whether it leaves Blackmail. */
export interface PathMove {
  to: FinalePath
  /** The path moved from (absent on the first commit). */
  from?: FinalePath
  /** The move leaves Blackmail: retaliation × `leverage.json blackmailExitMult`, Guardians' shorter floor, Sell × 0.6. */
  blackmailExit: boolean
}

/** Countdown ids the finale owns (one each per career). */
export const FINALE_COUNTDOWN = {
  guardianFloor: 'finale:guardianFloor',
  meridianGrievance: 'finale:meridianGrievance',
  surcharge: 'finale:surcharge',
} as const

const BAN: MeasureName = 'tetherstoneBan'

function empty(career: Career): StoryResult<Career> {
  return { career, commands: [], effects: [] }
}

function withFinale(career: Career, patch: (f: StoryFinale) => StoryFinale): Career {
  return withStory(career, (s) => ({ ...s, finale: patch(s.finale) }))
}

// ---------------------------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------------------------

/** True while Blackmail is in force (the stipend runs only then; S25 pays it). */
export function stipendActive(finale: StoryFinale): boolean {
  return pathsInForce(finale).includes('blackmail')
}

/** The stipend's inputs, fixed at the Blackmail commit (spec §8.2, EX-7): AI-only income × the share. */
export function fixStipend(input: FinaleInput, cfg: FinaleConfig, onlineDay: number): StoryStipend {
  const base = Math.max(0, input.view.tethysAiTetherIncome10d)
  return { base, share: cfg.blackmail.stipendShare, fixedOnlineDay: onlineDay, paid: 0 }
}

/**
 * Tethys's retaliation for a path (spec §8.3 "Retaliation lands once"): the first time, a Tethys → player pressure
 * seed plus `applyRetaliation` at `mult`; after that only what `applyRetaliation` finds new, never the seed again.
 */
export function landRetaliation(career: Career, input: FinaleInput, mult: number, seed: number, data: FinaleData): StoryResult<Career> {
  const first = storyOf(career).retaliation === undefined
  const r = applyRetaliation(
    career,
    { corpFaction: input.view.corpFaction, fleetNotesPrincipal: input.fleetNotesPrincipal, systemId: input.systemId, mult },
    data.leverage,
  )
  if (!first || seed === 0) return r
  return { career: r.career, commands: [{ type: 'seedPressure', a: 'tethys', b: 'player', amount: seed }, ...r.commands], effects: r.effects }
}

function retaliationMult(move: PathMove, data: FinaleData): number {
  return move.blackmailExit ? data.leverage.leverage.retaliation.blackmailExitMult : 1
}

/** A `floor` countdown and its command: due `days` online days from now (re-sent on re-apply and after an absence). */
function scheduleFloor(career: Career, id: string, ref: string, days: number, cmd: StoryCommand): StoryResult<Career> {
  return { career: scheduleCountdown(career, { id, kind: 'floor', inOnlineDays: days, ref }), commands: [cmd], effects: [] }
}

function merge(a: StoryResult<Career>, b: StoryResult<Career>): StoryResult<Career> {
  return { career: b.career, commands: [...a.commands, ...b.commands], effects: [...a.effects, ...b.effects] }
}

// ---------------------------------------------------------------------------------------------
// The four acts
// ---------------------------------------------------------------------------------------------

/**
 * The vote's sponsor (spec §8.2–§8.3): the player; after Guardians (the pilot flies with the cell) the player
 * corporation if founded, else `expose.fallbackSponsor` (the Mutual, Kell's leak).
 */
export function exposeSponsor(career: Career, input: FinaleInput, cfg: FinaleConfig): FactionId | 'player' {
  if (!pathsInForce(storyOf(career).finale).includes('guardians')) return 'player'
  return input.view.corpFaction ?? cfg.expose.fallbackSponsor
}

/**
 * Expose. Call it with the finale already recording the move (so the credibility penalties apply: the stipend after
 * Blackmail, the sponsor flying with the guardians) and the Expose bundle picked (`bundle.ts pickBundle`).
 */
export function exposeWrites(career: Career, move: PathMove, input: FinaleInput, data: FinaleData): StoryResult<Career> {
  const cfg = data.finale
  const bundle = storyOf(career).bundles.find((b) => b.path === 'expose')
  const weight = evidenceWeight(career, bundle?.entries ?? [], data.case)
  const sponsor = exposeSponsor(career, input, cfg)
  let out: StoryResult<Career> = {
    career,
    commands: [{ type: 'proposeCharterVote', measure: BAN, sponsor, evidenceWeight: weight }],
    effects: [],
  }
  out = merge(out, exposeAuthority(out.career, { corpFaction: input.view.corpFaction }, data.hale))
  out = { ...out, career: withFinale(out.career, (f) => ({ ...f, vote: 'pending' })) }
  return merge(out, landRetaliation(out.career, input, retaliationMult(move, data), cfg.expose.tethysPressureSeed, data))
}

/** Blackmail: `voss_blackmailed` and the stipend fixed at commit. Never a ladder target (nothing returns to it). */
export function blackmailWrites(career: Career, input: FinaleInput, data: FinaleData): StoryResult<Career> {
  const stipend = fixStipend(input, data.finale, storyOf(career).onlineDays)
  return empty(withFinale(setFlag(career, 'voss_blackmailed'), (f) => ({ ...f, stipend })))
}

/** Join the guardians (Reyes drops to `cool` after Blackmail: "you cashed her cheques first"). */
export function guardiansWrites(career: Career, move: PathMove, input: FinaleInput, data: FinaleData): StoryResult<Career> {
  const g = data.finale.guardians
  const days = move.blackmailExit ? g.floorAfterBlackmailOnlineDays : g.floorOnlineDays
  const until = storyOf(career).onlineDays + days
  let next = setFlag(career, 'guardian_joined')
  next = withFinale(next, (f) => ({ ...f, floorUntilOnlineDay: until }))
  if (move.blackmailExit && storyOf(next).reyesState === 'ally') next = withStory(next, (s) => ({ ...s, reyesState: 'cool' }))
  let out = scheduleFloor(next, FINALE_COUNTDOWN.guardianFloor, floorRef.aggression('guardian', g.aggressionFloor), days, {
    type: 'setAggressionFloor',
    faction: 'guardian',
    value: g.aggressionFloor,
    untilOnlineDay: until,
  })
  const commands: StoryCommand[] = [
    { type: 'hiddenTransfer', from: g.hiddenTransfer.from, to: 'guardian', amount: g.hiddenTransfer.amount },
    { type: 'seedPressure', a: 'tethys', b: 'guardian', amount: g.tethysSecurityPressure },
    { type: 'seedBounty', faction: 'authority', amount: g.wantedBounty, reason: 'guardian-joined' },
  ]
  for (const id of input.corpClaimIds ?? []) {
    commands.push({ type: 'setClaimState', scope: 'claim', id, state: 'harbouring', untilOnlineDay: null })
  }
  out = { ...out, commands: [...out.commands, ...commands] }
  return merge(out, landRetaliation(out.career, input, retaliationMult(move, data), g.tethysPressureSeed, data))
}

/** The Meridian payout for this move (× `payoutAfterBlackmailMult` after Blackmail, "damaged goods"). */
export function sellPayout(move: PathMove, cfg: FinaleConfig): number {
  return Math.round(cfg.sell.payout * (move.blackmailExit ? cfg.sell.payoutAfterBlackmailMult : 1))
}

/** Sell to Meridian (final). */
export function sellWrites(career: Career, move: PathMove, input: FinaleInput, data: FinaleData): StoryResult<Career> {
  const s = data.finale.sell
  const until = storyOf(career).onlineDays + s.grievanceOnlineDays
  const payout = sellPayout(move, data.finale)
  const paid = payout > 0 ? withWallet(career, payout) : career
  let out = scheduleFloor(paid, FINALE_COUNTDOWN.meridianGrievance, floorRef.grievance('meridian', 'tethys', s.grievanceFloor), s.grievanceOnlineDays, {
    type: 'setGrievance',
    a: 'meridian',
    b: 'tethys',
    floor: s.grievanceFloor,
    untilOnlineDay: until,
  })
  out = { ...out, commands: [{ type: 'seedPressure', a: 'meridian', b: 'tethys', amount: s.meridianTethysPressure }, ...out.commands] }
  if (!move.blackmailExit) return out
  return merge(out, landRetaliation(out.career, input, retaliationMult(move, data), s.tethysPressureSeed, data))
}

// ---------------------------------------------------------------------------------------------
// After the commit
// ---------------------------------------------------------------------------------------------

/**
 * The Expose vote's result as the worker reports it (the story bridge calls it): records it, sets `ban_passed` or
 * `ban_failed` and re-derives the clock (ban passed 0.5, failed 2.0). Unchanged unless Expose is in force and the
 * outcome is new.
 */
export function recordVoteOutcome(career: Career, outcome: Exclude<VoteOutcome, 'pending'>, data: FinaleData = finaleData()): StoryResult<Career> {
  const finale = storyOf(career).finale
  if (!pathsInForce(finale).includes('expose') || finale.vote === outcome) return empty(career)
  const flag = outcome === 'passed' ? 'ban_passed' : 'ban_failed'
  const next = setFlag(
    withFinale(career, (f) => ({ ...f, vote: outcome })),
    flag,
  )
  const clock = syncClock(next, data.clock)
  const effects: StoryEffect[] = [{ type: 'autosave', reason: `finale:vote:${outcome}` }]
  return { career: clock.career, commands: [{ type: 'setWorldFlag', key: flag, value: true }, ...clock.commands], effects }
}

/**
 * Where the Expose hand-in happens (spec §3.1, §8.3): the hearing in person, or its fallbacks. With Guardians in
 * force the pilot is wanted, so the hand-in goes only through comms (Duarte, or Kell's Wire leak), never a dock.
 */
export function exposeHandInContact(career: Career, view: ContactView, cfg: ContactsConfig = commsConfig().contacts): ContactResolution {
  if (!pathsInForce(storyOf(career).finale).includes('guardians')) return contactFor(career, view, 'exposeHandIn', cfg)
  const options = cfg.interactions.exposeHandIn.filter((o) => o.channel !== 'dock')
  return contactFor(career, view, 'exposeHandIn', { ...cfg, interactions: { ...cfg.interactions, exposeHandIn: options } })
}
